import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card } from '@/components/ui/card';
import { Upload, FileJson, CheckCircle, XCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Progress } from '@/components/ui/progress';

interface MeasureUploadZoneProps {
  onUploadComplete?: () => void;
}

export function MeasureUploadZone({ onUploadComplete }: MeasureUploadZoneProps) {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    
    // Validate file type
    if (!file.name.endsWith('.json')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JSON file containing measure logic.',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const token = localStorage.getItem('auth_token');
      console.log('[MeasureUploadZone] Token from localStorage:', token);
      const authHeader = token ? `Bearer ${token}` : '';
      console.log('[MeasureUploadZone] Authorization header being sent:', authHeader);
      const response = await fetch(`${apiUrl}/api/measures/upload`, {
        method: 'POST',
        headers: {
          'Authorization': authHeader
          // Note: Don't set Content-Type for FormData, browser will set it with boundary
        },
        body: formData,
        credentials: 'same-origin'
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Upload failed');
      }

      const result = await response.json();
      
      setUploadStatus('success');
      toast({
        title: 'Measure uploaded successfully',
        description: `Processed ${result.measuresProcessed} measures from ${file.name}`
      });

      if (onUploadComplete) {
        onUploadComplete();
      }

      // Reset after success
      setTimeout(() => {
        setUploadStatus('idle');
        setUploadProgress(0);
      }, 3000);

    } catch (error) {
      setUploadStatus('error');
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload measure file',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  }, [toast, onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json']
    },
    maxFiles: 1,
    disabled: uploading
  });

  return (
    <Card 
      {...getRootProps()} 
      className={`
        relative overflow-hidden transition-all duration-200 cursor-pointer
        ${isDragActive ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}
        ${uploading ? 'cursor-not-allowed opacity-75' : ''}
      `}
    >
      <input {...getInputProps()} />
      
      <div className="p-8">
        <div className="flex flex-col items-center justify-center space-y-4">
          {uploadStatus === 'idle' && (
            <>
              <div className="p-4 rounded-full bg-primary/10">
                <Upload className="h-8 w-8 text-primary" />
              </div>
              <div className="text-center">
                <p className="text-lg font-medium">
                  {isDragActive ? 'Drop the JSON file here' : 'Upload Measure Logic'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Drag & drop a measure logic JSON file here, or click to browse
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Supports CMS measure definition format (.json)
                </p>
              </div>
            </>
          )}

          {uploadStatus === 'uploading' && (
            <>
              <div className="p-4 rounded-full bg-blue-100 animate-pulse">
                <FileJson className="h-8 w-8 text-blue-600" />
              </div>
              <div className="w-full max-w-xs space-y-2">
                <p className="text-sm font-medium text-center">Uploading measure file...</p>
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-xs text-muted-foreground text-center">{uploadProgress}%</p>
              </div>
            </>
          )}

          {uploadStatus === 'success' && (
            <>
              <div className="p-4 rounded-full bg-green-100">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="text-center">
                <p className="text-lg font-medium text-green-600">Upload Successful!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Measure logic has been processed and stored
                </p>
              </div>
            </>
          )}

          {uploadStatus === 'error' && (
            <>
              <div className="p-4 rounded-full bg-red-100">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <div className="text-center">
                <p className="text-lg font-medium text-red-600">Upload Failed</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Please check the file format and try again
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
